import { FiberManualRecord, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import type { Patient } from "@rescuetablet/missions-client";
import Fuse from "fuse.js";
import { type PropsWithChildren, useCallback, useMemo, useState } from "react";

type SortField = "label" | "address" | "transportTarget";
type SortDirection = "asc" | "desc";

type Sort = {
  field: SortField;
  direction: SortDirection;
};

export default function PatientsTable({
  patients,
}: {
  patients: ReadonlyArray<Patient>;
}) {
  const [search, setSearch] = useState("");
  const index = useSearchIndex(patients);
  const filtered = useMemo(
    () =>
      search.trim().length ? index.search(search).map((h) => h.item) : patients,
    [index, patients, search]
  );

  const [sort, setSort] = useState<Sort>({ field: "label", direction: "asc" });
  const results = useMemo(
    () =>
      filtered.toSorted((a, b) => {
        const va = sort.field === "label" ? getLabel(a) : (a[sort.field] ?? "");
        const vb = sort.field === "label" ? getLabel(b) : (b[sort.field] ?? "");
        if (va === vb) return 0;
        return sort.direction === "asc" ? (va > vb ? 1 : -1) : va < vb ? 1 : -1;
      }),
    [filtered, sort]
  );

  return (
    <>
      <Box sx={{ px: 2 }}>
        <TextField
          type="search"
          label="Suche"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>
      {filtered.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <SortHeader field="label" sort={sort} setSort={setSort}>
                  Name
                </SortHeader>
              </TableCell>
              <TableCell>
                <SortHeader field="address" sort={sort} setSort={setSort}>
                  Adresse
                </SortHeader>
              </TableCell>
              <TableCell>
                <SortHeader
                  field="transportTarget"
                  sort={sort}
                  setSort={setSort}
                >
                  Transportziel
                </SortHeader>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((patient) => (
              <TableRow key={patient.id}>
                <TableCell
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PatientStatus patient={patient} />
                  <span>{getLabel(patient)}</span>
                </TableCell>
                <TableCell>{patient.address}</TableCell>
                <TableCell>{patient.transportTarget}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Keine Patienten gefunden
        </Alert>
      )}
    </>
  );
}

function PatientStatus({ patient }: { patient: Patient }) {
  const color =
    patient.condition === undefined
      ? "secondary"
      : patient.condition === "triage" || patient.condition === "in-treatment"
        ? "warning"
        : "success";

  return <FiberManualRecord color={color} />;
}

function useSearchIndex(patients: ReadonlyArray<Patient>) {
  return useMemo(
    () =>
      new Fuse(patients, {
        keys: ["identifier", "name", "address", "transportTarget"],
        shouldSort: false,
      }),
    [patients]
  );
}

function SortHeader({
  field,
  sort,
  setSort,
  children,
}: PropsWithChildren<{
  field: SortField;
  sort: Sort;
  setSort: (sort: Sort) => void;
}>) {
  const updateSort = useCallback(() => {
    if (field === sort.field) {
      setSort({ field, direction: sort.direction === "asc" ? "desc" : "asc" });
    } else {
      setSort({ field, direction: "asc" });
    }
  }, [field, sort, setSort]);

  return (
    <TableSortLabel
      direction={field === sort.field ? sort.direction : undefined}
      active={field === sort.field}
      onClick={updateSort}
    >
      {children}
    </TableSortLabel>
  );
}

function getLabel(patient: Patient): string {
  return (
    [patient.identifier, patient.name].filter(Boolean).join(", ") || patient.id
  );
}
